import { useState, useEffect } from 'react';
import { CalendarDialog } from './CalendarDialog';
import { makeReservation, clearError } from '../redux/slices/myselfSlice';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingModal } from './LoadingModal';

const CreateReservationModal = ({
  setShowCreateReservationModal,
  renterName,
  equipment,
  quantity,
  userId,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.myself.loading);
  const myselfError = useSelector((state) => state.myself.error);
  const [formData, setFormData] = useState({
    deliveryAssistance: '',
    pickupAssistance: '',
    startDate: '',
    endDate: '',
  });
  const [showTransportConfirmation, setShowTransportConfirmation] =
    useState(false);
  const [transportConfirmationChecked, setTransportConfirmationChecked] =
    useState(false);

  useEffect(() => {
    // mounts
    dispatch(clearError());

    // component unmounts
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDismissError = () => {
    dispatch(clearError());
  };

  const handleDateRangeChange = (range) => {
    //If user clicks the selected start date, it deselects it
    if (!range || !range.from) {
      setFormData({
        ...formData,
        startDate: '',
        endDate: '',
      });
      return;
    }

    setFormData({
      ...formData,
      startDate: range.from,
      endDate: range.to || '',
    });
  };

  const handleCloseModal = () => {
    dispatch(clearError());
    setShowCreateReservationModal(false);
  };

  const needsTransportConfirmation = () => {
    return (
      formData.deliveryAssistance === 'no' || formData.pickupAssistance === 'no'
    );
  };

  const handleSubmitClick = (e) => {
    e.preventDefault();
    if (needsTransportConfirmation() && !showTransportConfirmation) {
      setShowTransportConfirmation(true);
    } else {
      handleCreateReservation();
    }
  };

  const handleTransportConfirmationClose = () => {
    setShowTransportConfirmation(false);
    setTransportConfirmationChecked(false);
  };

  const handleCreateReservation = async () => {
    dispatch(
      makeReservation({
        userId: userId,
        equipmentId: equipment._id,
        qty: quantity,
        rentalPrice: equipment.rentalPrice * quantity,
        startDate: formData.startDate,
        endDate: formData.endDate,
        deliveryAssistance: formData.deliveryAssistance,
        pickupAssistance: formData.pickupAssistance,
      }),
    ).then((result) => {
      if (!result.error) {
        setShowCreateReservationModal(false);
      }
    });
  };

  const handleConfirmationCheckboxChange = (e) => {
    setTransportConfirmationChecked(e.target.checked);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      {(loading || myselfError) && (
        <LoadingModal
          message="Creating reservation..."
          error={myselfError}
          onClose={handleDismissError}
        />
      )}

      {/* Transport Confirmation Popup */}
      {showTransportConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg border-4 border-red-500 shadow-xl w-11/12 max-w-md p-6 outline outline-2 outline-gray-200">
            <h3 className="text-xl font-semibold mb-4 text-red-600">
              Self-Transport Confirmation
            </h3>
            <p className="mb-4 text-gray-700">
              You have selected to handle transport for this equipment{' '}
              <strong>without</strong> Farmshare assistance. Please confirm that
              you understand you will be responsible for transporting the
              equipment by <strong>yourself</strong> without assistance from
              Farmshare unless otherwise arranged.
            </p>

            {equipment.selfTransport && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <h4 className="font-semibold text-gray-800 mb-1">
                  Transport Requirements:
                </h4>
                <p className="text-gray-700">{equipment.selfTransport}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={transportConfirmationChecked}
                  onChange={handleConfirmationCheckboxChange}
                  className="form-checkbox h-5 w-5 text-green-500"
                />
                <span className="text-gray-800">
                  I understand and accept responsibility for transporting this
                  equipment
                </span>
              </label>
            </div>

            <div className="flex justify-end">
              <button
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
                onClick={handleTransportConfirmationClose}
                type="button"
              >
                Cancel
              </button>
              <button
                className={`bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-5 ml-4 rounded-md transition duration-200 ${
                  !transportConfirmationChecked
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
                onClick={handleCreateReservation}
                type="button"
                disabled={!transportConfirmationChecked}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Reservation Modal */}
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-md p-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">
          Reservation Information
        </h2>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Renter Name:</span>
            <span className="text-gray-900">{renterName}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Equipment:</span>
            <span className="text-gray-900">{equipment.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Location:</span>
            <span className="text-gray-900">{equipment.location}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Quantity:</span>
            <span className="text-gray-900">{quantity}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Price:</span>
            <span className="text-gray-900">
              ${equipment.rentalPrice * quantity}/day
            </span>
          </div>
          <div className="flex justify-center mt-4">
            {equipment && equipment._id && (
              <CalendarDialog
                value={{
                  from: formData.startDate || null,
                  to: formData.endDate || null,
                }}
                onChange={handleDateRangeChange}
                equipmentId={equipment._id}
              />
            )}
          </div>
          <form onSubmit={handleSubmitClick}>
            <div className="flex items-center justify-between gap-4 my-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">Start Date:</span>
                <span className="text-gray-900 font-bold">
                  {formData.startDate
                    ? new Date(formData.startDate).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-700">End Date:</span>
                <span className="text-gray-900 font-bold">
                  {formData.endDate
                    ? new Date(formData.endDate).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <label className="font-medium text-gray-700 flex items-center">
                Delivery Assistance?
                <span
                  title="lorem ipsum dolor sit amet, consectetur adipiscing elit"
                  className="ml-1 text-gray-500 cursor-help inline-flex items-center justify-center rounded-full bg-gray-100 w-4 h-4 text-xs"
                >
                  ?
                </span>
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="deliveryAssistance"
                    value="yes"
                    checked={formData.deliveryAssistance === 'yes'}
                    onChange={handleInputChange}
                    className="form-radio"
                  />
                  <span className="text-gray-900">Yes</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="deliveryAssistance"
                    value="no"
                    checked={formData.deliveryAssistance === 'no'}
                    onChange={handleInputChange}
                    className="form-radio"
                  />
                  <span className="text-gray-900">No</span>
                </label>
              </div>
            </div>
            <div className="flex justify-between items-center mt-4">
              <label className="font-medium text-gray-700">
                Pickup Assistance?
                <span
                  title="lorem ipsum dolor sit amet, consectetur adipiscing elit"
                  className="ml-1 text-gray-500 cursor-help inline-flex items-center justify-center rounded-full bg-gray-100 w-4 h-4 text-xs"
                >
                  ?
                </span>
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="pickupAssistance"
                    value="yes"
                    checked={formData.pickupAssistance === 'yes'}
                    onChange={handleInputChange}
                    className="form-radio"
                  />
                  <span className="text-gray-900">Yes</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="pickupAssistance"
                    value="no"
                    checked={formData.pickupAssistance === 'no'}
                    onChange={handleInputChange}
                    className="form-radio"
                  />
                  <span className="text-gray-900">No</span>
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
                onClick={handleCloseModal}
                type="button"
              >
                Close
              </button>
              <button
                className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-5 ml-4 rounded-md transition duration-200"
                type="submit"
              >
                Confirm
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { CreateReservationModal };
